import type { Point } from '../game/world-types.js';

export interface Rectangle extends Point { width: number; depth: number }
export interface Circle extends Point { radius: number; id?: string }
export const GEOMETRY_EPSILON = 1e-7;
export const distance = (a: Point, b: Point) => Math.sqrt((a.x - b.x) ** 2 + (a.z - b.z) ** 2);
export const surfaceDistance = (a: Circle, b: Circle) => Math.max(0, distance(a, b) - a.radius - b.radius);

export function pointSegmentDistance(point: Point, a: Point, b: Point): number {
  const dx = b.x - a.x, dz = b.z - a.z, lengthSquared = dx * dx + dz * dz;
  const t = lengthSquared === 0 ? 0 : Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.z - a.z) * dz) / lengthSquared));
  return Math.hypot(point.x - a.x - t * dx, point.z - a.z - t * dz);
}

export function rectangleCorners(rect: Rectangle, padding = 0): Point[] {
  const x = rect.width / 2 + padding, z = rect.depth / 2 + padding;
  return [{ x: rect.x - x, z: rect.z - z }, { x: rect.x + x, z: rect.z - z },
    { x: rect.x + x, z: rect.z + z }, { x: rect.x - x, z: rect.z + z }];
}

export function insideRectangle(point: Point, rect: Rectangle): boolean {
  return Math.abs(point.x - rect.x) <= rect.width / 2 && Math.abs(point.z - rect.z) <= rect.depth / 2;
}

// Module-level helpers avoid allocating closures on every segment test.
function endpointSquared(point: Point, left: number, right: number, top: number, bottom: number): number {
  const x = Math.max(left - point.x, 0, point.x - right), z = Math.max(top - point.z, 0, point.z - bottom);
  return x * x + z * z;
}
function cornerSquared(x: number, z: number, a: Point, dx: number, dz: number, lengthSquared: number): number {
  const t = lengthSquared === 0 ? 0 : Math.max(0, Math.min(1, ((x - a.x) * dx + (z - a.z) * dz) / lengthSquared));
  const offsetX = x - a.x - t * dx, offsetZ = z - a.z - t * dz;
  return offsetX * offsetX + offsetZ * offsetZ;
}

/**
 * Whether a segment passes within `margin` of a rectangle, as `segmentRectangleDistance(...) <= margin` but with the rectangle
 * grown square-cornered, which differs only by a fraction of `margin` at its corners. Clipping alone, for hot sight tests.
 */
export function segmentTouchesRectangle(a: Point, b: Point, rect: Rectangle, margin = GEOMETRY_EPSILON): boolean {
  const halfWidth = rect.width / 2 + margin, halfDepth = rect.depth / 2 + margin, dx = b.x - a.x, dz = b.z - a.z;
  let low = 0, high = 1;
  if (dx === 0) { if (Math.abs(a.x - rect.x) > halfWidth) return false; }
  else {
    const first = (rect.x - halfWidth - a.x) / dx, second = (rect.x + halfWidth - a.x) / dx;
    low = Math.max(low, Math.min(first, second)); high = Math.min(high, Math.max(first, second));
  }
  if (dz === 0) { if (Math.abs(a.z - rect.z) > halfDepth) return false; }
  else {
    const first = (rect.z - halfDepth - a.z) / dz, second = (rect.z + halfDepth - a.z) / dz;
    low = Math.max(low, Math.min(first, second)); high = Math.min(high, Math.max(first, second));
  }
  return low <= high;
}

export function segmentRectangleDistance(a: Point, b: Point, rect: Rectangle): number {
  const left = rect.x - rect.width / 2, right = rect.x + rect.width / 2;
  const top = rect.z - rect.depth / 2, bottom = rect.z + rect.depth / 2;
  const dx = b.x - a.x, dz = b.z - a.z;
  // Clip the segment to the two rectangle slabs without allocating edge points.
  let low = 0, high = 1;
  if (dx === 0) { if (a.x < left || a.x > right) high = -1; }
  else {
    const first = (left - a.x) / dx, second = (right - a.x) / dx;
    low = Math.max(low, Math.min(first, second)); high = Math.min(high, Math.max(first, second));
  }
  if (dz === 0) { if (a.z < top || a.z > bottom) high = -1; }
  else {
    const first = (top - a.z) / dz, second = (bottom - a.z) / dz;
    low = Math.max(low, Math.min(first, second)); high = Math.min(high, Math.max(first, second));
  }
  if (low <= high) return 0;
  // Disjoint segments/rectangles have a closest endpoint or rectangle corner.
  const lengthSquared = dx * dx + dz * dz;
  const result = Math.sqrt(Math.min(endpointSquared(a, left, right, top, bottom), endpointSquared(b, left, right, top, bottom),
    cornerSquared(left, top, a, dx, dz, lengthSquared), cornerSquared(right, top, a, dx, dz, lengthSquared),
    cornerSquared(right, bottom, a, dx, dz, lengthSquared), cornerSquared(left, bottom, a, dx, dz, lengthSquared)));
  return result < GEOMETRY_EPSILON ? 0 : result;
}

export interface NavigationSpace { width: number; depth: number; obstacles: Rectangle[] }

// Large maps hold hundreds of obstacles and every route search issues thousands of segment tests, so
// the obstacle list is bucketed into a uniform grid the first time a long list is queried. A query only
// visits obstacles that share a cell with the segment's padded bounds; those are exactly the obstacles
// the plain scan's bounds test would keep, so results are identical. The index is keyed by the array and
// rebuilt when its length changes; replace the array (as `{ ...space, obstacles: [...] }` does) rather
// than editing an obstacle in place after it has been queried.
const GRID_CELL = 16, GRID_MIN_OBSTACLES = 32;
interface ObstacleGrid { length: number; minX: number; minZ: number; columns: number; rows: number; cells: number[][]; seen: Uint32Array; pass: number }
const grids = new WeakMap<Rectangle[], ObstacleGrid>();

function gridFor(obstacles: Rectangle[]): ObstacleGrid {
  const cached = grids.get(obstacles);
  if (cached && cached.length === obstacles.length) return cached;
  let minX = Infinity, minZ = Infinity, maxX = -Infinity, maxZ = -Infinity;
  for (const rect of obstacles) {
    minX = Math.min(minX, rect.x - rect.width / 2); maxX = Math.max(maxX, rect.x + rect.width / 2);
    minZ = Math.min(minZ, rect.z - rect.depth / 2); maxZ = Math.max(maxZ, rect.z + rect.depth / 2);
  }
  const columns = Math.floor((maxX - minX) / GRID_CELL) + 1, rows = Math.floor((maxZ - minZ) / GRID_CELL) + 1;
  const cells: number[][] = Array.from({ length: columns * rows }, () => []);
  obstacles.forEach((rect, index) => {
    const x0 = Math.floor((rect.x - rect.width / 2 - minX) / GRID_CELL), x1 = Math.floor((rect.x + rect.width / 2 - minX) / GRID_CELL);
    const z0 = Math.floor((rect.z - rect.depth / 2 - minZ) / GRID_CELL), z1 = Math.floor((rect.z + rect.depth / 2 - minZ) / GRID_CELL);
    for (let column = x0; column <= x1; column++) for (let row = z0; row <= z1; row++) cells[row * columns + column].push(index);
  });
  const grid = { length: obstacles.length, minX, minZ, columns, rows, cells, seen: new Uint32Array(obstacles.length), pass: 0 };
  grids.set(obstacles, grid);
  return grid;
}

function obstructed(obstacles: Rectangle[], a: Point, b: Point, padding: number, minX: number, maxX: number, minZ: number, maxZ: number, inclusive = false): boolean {
  const test = (rect: Rectangle) => rect.x + rect.width / 2 >= minX && rect.x - rect.width / 2 <= maxX &&
    rect.z + rect.depth / 2 >= minZ && rect.z - rect.depth / 2 <= maxZ &&
    (inclusive ? segmentRectangleDistance(a, b, rect) <= padding : segmentRectangleDistance(a, b, rect) < padding);
  if (obstacles.length < GRID_MIN_OBSTACLES) return obstacles.some(test);
  const grid = gridFor(obstacles);
  const x0 = Math.max(0, Math.floor((minX - grid.minX) / GRID_CELL)), x1 = Math.min(grid.columns - 1, Math.floor((maxX - grid.minX) / GRID_CELL));
  const dx = b.x - a.x, dz = b.z - a.z, pass = ++grid.pass;
  for (let column = x0; column <= x1; column++) {
    // Long diagonal segments cross few cells; take only the rows the segment reaches within this column.
    const left = grid.minX + column * GRID_CELL - padding, right = grid.minX + (column + 1) * GRID_CELL + padding;
    let t0 = 0, t1 = 1;
    if (dx !== 0) { const first = (left - a.x) / dx, second = (right - a.x) / dx; t0 = Math.max(0, Math.min(first, second)); t1 = Math.min(1, Math.max(first, second)); }
    if (t0 > t1) continue;
    const zA = a.z + dz * t0, zB = a.z + dz * t1;
    const z0 = Math.max(0, Math.floor((Math.min(zA, zB) - padding - grid.minZ) / GRID_CELL)), z1 = Math.min(grid.rows - 1, Math.floor((Math.max(zA, zB) + padding - grid.minZ) / GRID_CELL));
    for (let row = z0; row <= z1; row++) {
      for (const index of grid.cells[row * grid.columns + column]) {
        if (grid.seen[index] === pass) continue;
        grid.seen[index] = pass;
        if (test(obstacles[index])) return true;
      }
    }
  }
  return false;
}

/** Terrain-only sight test, using the same exact distance/tolerance as combat's original full scan. */
export function clearLineOfSight(obstacles: Rectangle[], a: Point, b: Point): boolean {
  return !obstructed(obstacles, a, b, GEOMETRY_EPSILON,
    Math.min(a.x, b.x) - GEOMETRY_EPSILON, Math.max(a.x, b.x) + GEOMETRY_EPSILON,
    Math.min(a.z, b.z) - GEOMETRY_EPSILON, Math.max(a.z, b.z) + GEOMETRY_EPSILON, true);
}

/** Occupied bases only; callers that already validated terrain need not repeat it. */
export function clearUnitSegment(a: Point, b: Point, radius: number, blockers: Circle[]): boolean {
  return !blockers.some(circle => pointSegmentDistance(circle, a, b) < circle.radius + radius - GEOMETRY_EPSILON);
}

export function clearSegment(space: NavigationSpace, a: Point, b: Point, radius: number, blockers: Circle[] = []): boolean {
  if (!(Number.isFinite(a.x) && Number.isFinite(a.z) && Number.isFinite(b.x) && Number.isFinite(b.z) && Number.isFinite(radius)) || radius < 0) return false;
  const limitX = space.width / 2 + GEOMETRY_EPSILON - radius, limitZ = space.depth / 2 + GEOMETRY_EPSILON - radius;
  if (Math.abs(a.x) > limitX || Math.abs(a.z) > limitZ || Math.abs(b.x) > limitX || Math.abs(b.z) > limitZ) return false;
  // Reject distant expanded bounds before the exact rounded-corner test.
  const padding = radius + GEOMETRY_EPSILON;
  const minX = Math.min(a.x, b.x) - padding, maxX = Math.max(a.x, b.x) + padding;
  const minZ = Math.min(a.z, b.z) - padding, maxZ = Math.max(a.z, b.z) + padding;
  if (obstructed(space.obstacles, a, b, padding, minX, maxX, minZ, maxZ)) return false;
  return clearUnitSegment(a, b, radius, blockers);
}
